import { useCallback, useEffect, useState } from 'react';

import { api, type SzolgaltatasLista, type SzolgaltatasSor } from '../api.ts';
import { useMuvelet } from '../felhasznalo-context.ts';
import { ft } from '../formazas.ts';
import { letolt } from '../letoltes.ts';

/**
 * Ki milyen szolgáltatást kap. (2026-10-08, a megrendelő kérése)
 *
 * Sales-munkalista: soronként egy partner, oszloponként egy szolgáltatás, a
 * cellában „x" vagy „·". Minden oszlop fölött egy kis választó (mind / van /
 * nincs), így a kérdések szabadon összeállíthatók — „vesz olajat, de nincs
 * szerződése", „van szerződése, de nem vesz", és így tovább.
 *
 * A négy szám a fejlécben egy-egy kész kérdés: rákattintva beállítja a hozzá
 * tartozó szűrést, hogy ne kelljen összekattintani.
 */

/** A szűrés egy állapota. Ami „mind", az nem megy ki a kérésbe. */
interface Szuro {
  olaj: string;
  fo_szerz: string;
  mo_szerz: string;
  hso_gyujt: string;
  mo_gyujt: string;
  nyilatkozat: string;
}

const URES: Szuro = {
  olaj: 'mind',
  fo_szerz: 'mind',
  mo_szerz: 'mind',
  hso_gyujt: 'mind',
  mo_gyujt: 'mind',
  nyilatkozat: 'mind',
};

/** A kész kérdések — a fejléc számaihoz. */
const KERDESEK: Array<{ kulcs: string; cimke: string; cim: string; szuro: Partial<Szuro> }> = [
  {
    kulcs: 'vesz_de_nincs_szerz',
    cimke: 'Vesz olajat, nincs szerződése',
    cim: 'Olajat vásárolt az időszakban, de se MO, se FO szerződése nincs érvényben.',
    szuro: { olaj: 'van', fo_szerz: 'nincs', mo_szerz: 'nincs' },
  },
  {
    kulcs: 'szerz_de_nem_vesz',
    cimke: 'Van szerződése, nem vesz',
    cim: 'Van érvényes szerződése, de az időszakban nem vásárolt olajat.',
    szuro: { olaj: 'nincs', fo_szerz: 'van' },
  },
  {
    kulcs: 'gyujtunk_de_nincs_szerz',
    cimke: 'Gyűjtünk tőle, nincs szerződése',
    cim: 'Állítottunk ki rá begyűjtő szállítólevelet, de nincs érvényes szerződése.',
    szuro: { hso_gyujt: 'van', fo_szerz: 'nincs', mo_szerz: 'nincs' },
  },
  {
    kulcs: 'semmi',
    cimke: 'Nincs kapcsolat',
    cim: 'Nem vásárolt, nem gyűjtünk tőle, és nincs szerződése — alvó partner.',
    szuro: { olaj: 'nincs', hso_gyujt: 'nincs', mo_gyujt: 'nincs', fo_szerz: 'nincs', mo_szerz: 'nincs' },
  },
];

const HARMAS = [
  ['mind', 'mind'],
  ['van', 'van'],
  ['nincs', 'nincs'],
] as const;

const NEGYES = [
  ['mind', 'mind'],
  ['van', 'van'],
  ['lejart', 'lejárt'],
  ['nincs', 'nincs'],
] as const;

/** Egy pipás cella. */
function Jel({ van }: { van: boolean }) {
  return van ? <b style={{ color: 'var(--ok)' }}>x</b> : <span style={{ color: 'var(--ghost)' }}>·</span>;
}

/** A szerződés három állapota egy cellában. */
function SzerzJel({ ertek }: { ertek: SzolgaltatasSor['fo_szerz'] }) {
  if (ertek === 'van') return <b style={{ color: 'var(--ok)' }}>x</b>;
  if (ertek === 'lejart') return <b style={{ color: 'var(--flag)' }}>lejárt</b>;
  return <span style={{ color: 'var(--ghost)' }}>·</span>;
}

export function SzolgaltatasMatrix({ onKarton }: { onKarton: (etterem_id: number) => void }) {
  const letolthet = useMuvelet('export_kimutatas');
  const [szuro, setSzuro] = useState<Szuro>(URES);
  const [kereses, setKereses] = useState('');
  const [honapok, setHonapok] = useState(12);
  const [aktiv, setAktiv] = useState<'aktiv' | 'mind'>('aktiv');
  const [lista, setLista] = useState<SzolgaltatasLista | null>(null);
  const [hiba, setHiba] = useState<string | null>(null);

  /** A kérés paraméterei — a „mind" értékek kimaradnak. */
  const parameterek = useCallback(() => {
    const p = new URLSearchParams();
    if (kereses.trim()) p.set('q', kereses.trim());
    p.set('honapok', String(honapok));
    if (aktiv === 'mind') p.set('aktiv', 'mind');
    for (const [k, v] of Object.entries(szuro)) if (v !== 'mind') p.set(k, v);
    return p;
  }, [kereses, honapok, aktiv, szuro]);

  const betolt = useCallback(async () => {
    try {
      setLista(await api.get<SzolgaltatasLista>(`/api/attekintes/szolgaltatas?${parameterek()}`));
      setHiba(null);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A kimutatást nem sikerült betölteni.');
    }
  }, [parameterek]);

  useEffect(() => {
    const idozo = setTimeout(() => void betolt(), 200);
    return () => clearTimeout(idozo);
  }, [betolt]);

  const sorok = lista?.sorok ?? [];
  const o = lista?.osszesites;
  /* Melyik kész kérdés van épp beállítva — azt kiemeljük. */
  const aktivKerdes = KERDESEK.find((k) =>
    Object.entries({ ...URES, ...k.szuro }).every(([m, v]) => szuro[m as keyof Szuro] === v),
  )?.kulcs;

  const valaszto = (
    mezo: keyof Szuro,
    lehetosegek: readonly (readonly [string, string])[],
  ) => (
    <select
      className="celvalaszto"
      value={szuro[mezo]}
      onChange={(e) => setSzuro({ ...szuro, [mezo]: e.target.value })}
    >
      {lehetosegek.map(([k, cimke]) => (
        <option key={k} value={k}>
          {cimke}
        </option>
      ))}
    </select>
  );

  return (
    <>
      <div className="dayhead">
        <input
          className="qi"
          style={{ width: 280, fontSize: 12.5 }}
          value={kereses}
          placeholder="Keresés név, cím, cégnév, adószám…"
          onChange={(e) => setKereses(e.target.value)}
        />
        <label style={{ fontSize: 12, color: 'var(--muted)' }}>
          Vásárlás ennyi hónapra visszamenve{' '}
          <select value={honapok} onChange={(e) => setHonapok(Number(e.target.value))}>
            {[3, 6, 12, 24, 36].map((h) => (
              <option key={h} value={h}>
                {h}
              </option>
            ))}
          </select>
        </label>
        <label style={{ fontSize: 12, color: 'var(--muted)' }}>
          <input
            type="checkbox"
            checked={aktiv === 'mind'}
            onChange={(e) => setAktiv(e.target.checked ? 'mind' : 'aktiv')}
          />{' '}
          inaktívak is
        </label>
        {letolthet && (
          <button
            className="btn sm"
            onClick={() => letolt(`/api/export/szolgaltatas?${parameterek()}`)}
          >
            Excel
          </button>
        )}
      </div>

      {/*
        * A négy kész kérdés. Rákattintva beállítja a szűrést — a szám pedig
        * akkor is a teljes törzsre vonatkozik, ha épp szűrve van.
        */}
      <div className="fchips">
        {KERDESEK.map((k) => (
          <button
            key={k.kulcs}
            className={`fchip${aktivKerdes === k.kulcs ? ' on' : ''}`}
            title={k.cim}
            onClick={() =>
              setSzuro(aktivKerdes === k.kulcs ? URES : { ...URES, ...k.szuro })
            }
          >
            {k.cimke}
            <b>{ft((o as Record<string, number> | undefined)?.[k.kulcs] ?? 0)}</b>
          </button>
        ))}
        <button className="fchip" onClick={() => setSzuro(URES)}>
          Szűrés törlése
        </button>
        <span className="hint">
          {ft(lista?.talalat ?? 0)} partner a {ft(lista?.osszes ?? 0)} közül
          {lista && ` · olajvásárlás: ${lista.idoszak.tol} – ${lista.idoszak.ig}`}
        </span>
      </div>

      {hiba && <div className="uzenet bad">{hiba}</div>}

      <div className="matrix" style={{ maxHeight: 620, overflow: 'auto' }}>
        <table className="tt2 szolgm">
          <thead>
            <tr>
              <th>Partner</th>
              <th className="c">Olajat vesz</th>
              <th className="n">Liter</th>
              <th className="n">Utolsó vásárlás</th>
              <th className="c">FO szerződés</th>
              <th className="c">Nyilatkozat</th>
              <th className="c">MO szerződés</th>
              <th className="c">FO gyűjtés</th>
              <th className="c">MO gyűjtés</th>
              <th>Telefon</th>
              <th>E-mail</th>
            </tr>
            <tr className="szuro">
              <th />
              <th className="c">{valaszto('olaj', HARMAS)}</th>
              <th />
              <th />
              <th className="c">{valaszto('fo_szerz', NEGYES)}</th>
              <th className="c">{valaszto('nyilatkozat', HARMAS)}</th>
              <th className="c">{valaszto('mo_szerz', NEGYES)}</th>
              <th className="c">{valaszto('hso_gyujt', HARMAS)}</th>
              <th className="c">{valaszto('mo_gyujt', HARMAS)}</th>
              <th />
              <th />
            </tr>
          </thead>
          <tbody>
            {sorok.map((s) => (
              <tr key={s.etterem_id} className={s.aktiv ? undefined : 'nul'}>
                <td className="nm3" onClick={() => onKarton(s.etterem_id)} title="Partnerkarton">
                  <b>{s.nev}</b>
                  {(s.cegnev || s.cim) && (
                    <small>{[s.cegnev, s.cim].filter(Boolean).join(' · ')}</small>
                  )}
                  {!s.van_ar && <small className="only">nincs ára</small>}
                </td>
                <td className="c">
                  <Jel van={s.olaj} />
                </td>
                <td className="calc2">{s.olaj_liter ? ft(s.olaj_liter) : '·'}</td>
                <td className="calc2">{s.olaj_utolso ?? '—'}</td>
                <td className="c">
                  <SzerzJel ertek={s.fo_szerz} />
                </td>
                <td className="c">
                  <Jel van={s.nyilatkozat} />
                </td>
                <td className="c">
                  <SzerzJel ertek={s.mo_szerz} />
                </td>
                <td className="c">
                  <Jel van={s.hso_gyujtve} />
                </td>
                <td className="c">
                  <Jel van={s.mo_gyujtve} />
                </td>
                <td>{s.telefon ?? <span style={{ color: 'var(--ghost)' }}>·</span>}</td>
                <td>
                  {s.email_szamla ?? s.email_szallito ?? s.email_egyeb ?? (
                    <span style={{ color: 'var(--ghost)' }}>·</span>
                  )}
                </td>
              </tr>
            ))}
            {sorok.length === 0 && (
              <tr>
                <td colSpan={11} style={{ padding: 26, textAlign: 'center', color: '#A3A9B0' }}>
                  {lista === null ? 'Betöltés…' : 'Erre a szűrésre nincs partner.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="uzenet flag" style={{ marginTop: 14 }}>
        <b>A „gyűjtés" tény, nem szándék:</b> akkor áll x, ha tényleg állítottunk ki rá begyűjtő
        szállítólevelet. A <b>lejárt</b> szerződés nem számít „nincs"-nek — azt külön lehet
        kérdezni. Az Excelben az elérhetőségek is benne vannak, e-mail-kategóriánként.
      </div>
    </>
  );
}
